// GOLD-12178 / GOLD-12179: Timed Matching Grid (SDMT) question UI.
// Digit: type 1–9 and auto-advance. Checkmark: tick only the checkbox
// strip and move with arrows (no hover). Next unlocks when complete or
// the timer ends; expire is not persisted until Next.
import React, { forwardRef, useCallback, useEffect, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import { FALLBACK_STRINGS } from 'utils/constants'
import SurveyQuestion from '../../SurveyQuestion'
import MakeScrollable from '../../MakeScrollable'
import QuestionFooter from '../../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion
} from '../../../utils/instrumentUtils'
import {
  ACTIONS,
  createInitialState,
  timedMatchingGridReducer
} from './timedMatchingGridReducer'
import {
  INPUT_TYPES,
  UNREACHED_SENTINEL,
  buildAnswerPayload,
  countAnswered,
  countTicked,
  formatTimerDisplay,
  isDigitChar,
  isRenderableImageSrc,
  countItemsSupervised,
  getTimedMatchingGridStoredAnswer,
  getTimedMatchingGridLeaveAction,
  hasAdvancedPastTimedMatchingGrid,
  isTimedMatchingGridFullyAnswered,
  isTimedMatchingGridSittingComplete,
  canEnableTimedMatchingGridNext
} from './timedMatchingGridUtils'
import './TimedMatchingGrid.scss'

// GOLD-12199: Persist the sequence contract (not UI cell objects) so
// server scoring can compare correct vs submitted arrays. in_review is
// forced false because a timed sitting is not re-editable from Review.
const buildSubmission = (props, payload, timedOut = false) => {
  const { question, id } = props
  const { label, type, attributes } = question
  const { required } = attributes || {}
  const submitted = payload?.submitted_item_sequence || []
  const hasProgress =
    submitted.some(value => value !== UNREACHED_SENTINEL) || timedOut

  return {
    id,
    label,
    name: id,
    type,
    required,
    state: hasProgress || timedOut ? 'ANSWERED' : 'SKIPPED',
    answer: [
      {
        input_type: payload.input_type,
        correct_item_sequence: payload.correct_item_sequence,
        submitted_item_sequence: payload.submitted_item_sequence,
        elapsed_seconds: payload.elapsed_seconds,
        timed_out: !!payload.timed_out
      }
    ],
    attributes: {
      ...(attributes || {}),
      in_review: false
    }
  }
}

// GOLD-12178: Reuse the shared skip-logic path so TMG Next behaves like
// other question types (review / finish / sibling gating).
const completeTimedMatchingQuestion = (props, payload) => {
  const {
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false
  } = props
  const submission = buildSubmission(props, payload, !!payload?.timed_out)
  const currentAnswer = { [id]: submission.answer }
  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling
  })
  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ
  )
}

// GOLD-12178: Save a finished grid without advancing so Back from a
// completed sitting still locks the answers when the participant returns.
const persistTimedMatchingQuestion = (props, payload) => {
  const { completeQuestion, webSurvey } = props
  if (typeof completeQuestion !== 'function' || !webSurvey) return
  const submission = buildSubmission(props, payload, !!payload?.timed_out)
  completeQuestion(submission, webSurvey.order?.[0])
}

// GOLD-12178: Prefer a resolved image; fall back to the authored character
// so a missing zip asset still shows something in the key and cells.
const SymbolContent = ({ entry }) => {
  if (!entry) return <span className='tmg-symbol-fallback'>?</span>
  if (isRenderableImageSrc(entry.image_url)) {
    return (
      <img
        className='tmg-symbol-image'
        src={entry.image_url}
        alt={`Symbol for ${entry.digit}`}
      />
    )
  }
  if (entry.character) {
    return <span className='tmg-symbol-char'>{entry.character}</span>
  }
  return <span className='tmg-symbol-fallback'>?</span>
}

SymbolContent.propTypes = {
  entry: PropTypes.shape({
    digit: PropTypes.number,
    character: PropTypes.string,
    image_url: PropTypes.string
  })
}

const TimedMatchingGrid = forwardRef((props, ref) => {
  const {
    className,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    strings = {},
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
    currentSurvey = [],
    isActiveQuestion = true
  } = props

  const { attributes = {}, label } = question || {}
  const liveAnswer = getTimedMatchingGridStoredAnswer({
    questionId: id,
    submissionSurvey,
    storedAnswer
  })
  // GOLD-12178: Lock after Next or after the participant has already
  // advanced past this question, so Back shows a read-only completed
  // grid instead of starting a second timed sitting.
  const isLocked =
    isTimedMatchingGridSittingComplete(liveAnswer) ||
    hasAdvancedPastTimedMatchingGrid(id, currentSurvey)

  const [state, dispatch] = useReducer(
    timedMatchingGridReducer,
    undefined,
    () =>
      createInitialState(attributes, isLocked ? liveAnswer : null, {
        locked: isLocked
      })
  )
  const hasSubmittedRef = useRef(false)
  const stateRef = useRef(state)
  const wasActiveRef = useRef(isActiveQuestion)
  const gridScrollRef = useRef(null)
  const cellRefs = useRef({})
  stateRef.current = state

  const {
    cells,
    focusIndex,
    elapsedSeconds,
    expired,
    timedOut,
    gridRows,
    gridColumns,
    timeLimitSeconds,
    timerDisplay,
    inputType,
    symbolKey,
    locked
  } = state

  const readOnly = expired || locked || isLocked

  const symbolByDigit = symbolKey.reduce((acc, entry) => {
    acc[entry.digit] = entry
    return acc
  }, {})

  const answered = countAnswered(cells)
  const ticked = countTicked(cells)
  const reviewed = countItemsSupervised(state)
  const total = cells.length
  const canAdvance = canEnableTimedMatchingGridNext(state)
  const counterText =
    inputType === INPUT_TYPES.checkmark
      ? `${reviewed} of ${total} reviewed. ${ticked} ticked wrong`
      : `${answered} of ${total} answered`

  // GOLD-12178: Same as Review — Applanga/strings first. Backup is
  // FALLBACK_STRINGS in constants.js (not inline) so the copy lives once.
  const guidance =
    inputType === INPUT_TYPES.checkmark
      ? strings.timed_matching_grid_checkmark_guidance ||
        FALLBACK_STRINGS.timed_matching_grid_checkmark_guidance
      : strings.timed_matching_grid_digit_guidance ||
        FALLBACK_STRINGS.timed_matching_grid_digit_guidance

  const timerLabel = formatTimerDisplay(
    elapsedSeconds,
    timeLimitSeconds,
    timerDisplay
  )

  const getPayload = useCallback(
    () => buildAnswerPayload(stateRef.current),
    []
  )

  // GOLD-12178: Keep the focused cell visible in tall grids so arrow
  // navigation does not land on a cell that is scrolled out of view.
  const scrollFocusedCellIntoView = useCallback(index => {
    const cellEl = cellRefs.current[index]
    const scrollEl = gridScrollRef.current
    if (!cellEl || !scrollEl) return

    const cellRect = cellEl.getBoundingClientRect()
    const scrollRect = scrollEl.getBoundingClientRect()

    if (cellRect.bottom > scrollRect.bottom) {
      scrollEl.scrollTop += cellRect.bottom - scrollRect.bottom + 12
    } else if (cellRect.top < scrollRect.top) {
      scrollEl.scrollTop -= scrollRect.top - cellRect.top + 12
    }
  }, [])

  useEffect(() => {
    if (readOnly) return
    scrollFocusedCellIntoView(focusIndex)
  }, [readOnly, focusIndex, scrollFocusedCellIntoView])

  // GOLD-12178: hasSubmittedRef blocks a double Next (footer + expire)
  // from posting two answers. Reset on the footer click so a retry after
  // a failed complete can run again.
  const submitAndAdvance = useCallback(
    (forceTimedOut = false) => {
      if (hasSubmittedRef.current) return
      hasSubmittedRef.current = true
      const payload = {
        ...getPayload(),
        timed_out: forceTimedOut || !!stateRef.current.timedOut
      }
      dispatch({ type: ACTIONS.LOCK })
      completeTimedMatchingQuestion(props, payload)
    },
    [getPayload, props]
  )

  useEffect(() => {
    if (!isLocked) return
    dispatch({ type: ACTIONS.LOCK })
  }, [isLocked])

  // GOLD-12178: Tick only while this question is on screen and unlocked.
  // Leaving Select One must not keep the countdown running in the background.
  useEffect(() => {
    if (readOnly || !isActiveQuestion) return undefined
    const intervalId = setInterval(() => {
      dispatch({ type: ACTIONS.TICK })
    }, 1000)
    return () => clearInterval(intervalId)
  }, [readOnly, isActiveQuestion])

  // GOLD-12178: Next/Back only scroll; TMG stays mounted. Run leave
  // logic only on the active → inactive transition so first paint and
  // parent rerenders do not restart a sitting the participant is taking.
  useEffect(() => {
    const justLeft = wasActiveRef.current && !isActiveQuestion
    wasActiveRef.current = isActiveQuestion
    if (!justLeft) return
    const leaveAction = getTimedMatchingGridLeaveAction({
      isActiveQuestion,
      isSittingLocked: isLocked || stateRef.current.locked,
      isGridComplete: isTimedMatchingGridFullyAnswered(stateRef.current),
      // GOLD-12409: Expire unlocks Next but does not fill the grid.
      // Treat it as finished so Back then forward keeps answers and
      // does not start a second timer.
      isExpired:
        !!stateRef.current.expired || !!stateRef.current.timedOut
    })
    if (leaveAction === 'lock') {
      if (!stateRef.current.locked) {
        persistTimedMatchingQuestion(props, getPayload())
        dispatch({ type: ACTIONS.LOCK })
      }
      return
    }
    if (leaveAction === 'restart') {
      hasSubmittedRef.current = false
      dispatch({
        type: ACTIONS.INIT,
        attributes,
        storedAnswer: null,
        locked: false
      })
    }
  }, [isActiveQuestion, isLocked, attributes, getPayload, props])

  // GOLD-12178: Listen on window so digit/arrow keys work without a
  // focused input. Checkmark does not type digits — only arrows move.
  // Left/right are reading-order ±1 so the last cell of a row continues
  // onto the next row (BA: no mouse-hover tracking).
  useEffect(() => {
    if (readOnly || !isActiveQuestion) return undefined
    const onKeyDown = event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return

      const {
        focusIndex: currentFocus,
        gridColumns: cols,
        gridRows: rows,
        inputType: mode
      } = stateRef.current

      // GOLD-12178: Horizontal arrows are linear (±1), not wrapped to
      // the next/prev row via column math, so End-of-row goes to the
      // first cell of the next row.
      const moveFocusByArrow = (deltaRow, deltaCol) => {
        const cellCount = stateRef.current.cells.length
        if (deltaRow === 0 && deltaCol !== 0) {
          const next = currentFocus + deltaCol
          if (next < 0 || next >= cellCount) return
          dispatch({ type: ACTIONS.FOCUS_CELL, index: next })
          return
        }
        const row = Math.floor(currentFocus / cols)
        const col = currentFocus % cols
        const nextRow = Math.max(0, Math.min(rows - 1, row + deltaRow))
        const nextCol = Math.max(0, Math.min(cols - 1, col + deltaCol))
        dispatch({
          type: ACTIONS.FOCUS_CELL,
          index: nextRow * cols + nextCol
        })
      }

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault()
          moveFocusByArrow(0, 1)
          return
        case 'ArrowLeft':
          event.preventDefault()
          moveFocusByArrow(0, -1)
          return
        case 'ArrowDown':
          event.preventDefault()
          moveFocusByArrow(1, 0)
          return
        case 'ArrowUp':
          event.preventDefault()
          moveFocusByArrow(-1, 0)
          return
        default:
          break
      }

      if (mode !== INPUT_TYPES.digit) return
      if (isDigitChar(event.key)) {
        event.preventDefault()
        dispatch({ type: ACTIONS.ENTER_DIGIT, digit: event.key })
        return
      }
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        dispatch({ type: ACTIONS.CLEAR_CELL })
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [readOnly, isActiveQuestion])

  const onFocusCell = index => {
    if (readOnly) return
    dispatch({ type: ACTIONS.FOCUS_CELL, index })
  }

  // GOLD-12178: Checkmark marks incorrect only via the checkbox strip.
  // Clicking the cell body must not tick, so scrolling/mis-clicks do
  // not change answers (BA: no hover tracking).
  const onToggleTick = index => {
    if (readOnly) return
    dispatch({ type: ACTIONS.TOGGLE_TICK, index })
  }

  const renderCell = cell => {
    // GOLD-12178: The cyan ring is the edit cursor. A locked sitting is
    // read-only, so keep focusIndex for restore but do not draw the ring.
    const isFocused = !readOnly && cell.index === focusIndex
    const entry = symbolByDigit[cell.symbol_digit]
    const isTicked = !!cell.ticked
    return (
      <div
        key={`cell-${cell.index}`}
        ref={el => {
          if (el) cellRefs.current[cell.index] = el
          else delete cellRefs.current[cell.index]
        }}
        className={`tmg-cell${isFocused ? ' focused' : ''}${
          isTicked ? ' ticked' : ''
        }`}
        role='gridcell'
        tabIndex={readOnly ? -1 : 0}
        aria-disabled={readOnly || undefined}
        aria-checked={
          inputType === INPUT_TYPES.checkmark ? isTicked : undefined
        }
        onClick={() => {
          // Digit and checkmark both move the review cursor here.
          // Checkmark still only ticks from the checkbox strip.
          if (readOnly) return
          onFocusCell(cell.index)
        }}
        onFocus={() => {
          if (readOnly) return
          onFocusCell(cell.index)
        }}>
        <div className='tmg-cell-symbol'>
          <SymbolContent entry={entry} />
        </div>
        {inputType === INPUT_TYPES.digit ? (
          <div className='tmg-cell-response'>
            <span className='tmg-response-value'>
              {cell.response ?? ''}
              {isFocused && !readOnly ? (
                <span className='tmg-cursor' aria-hidden='true' />
              ) : null}
            </span>
          </div>
        ) : (
          <div
            className='tmg-cell-check'
            onClick={event => {
              // GOLD-12178: stopPropagation so the cell click handler
              // never also runs. Focus + toggle together so this cell
              // is reviewed and marked wrong in one click.
              event.stopPropagation()
              if (readOnly) return
              onFocusCell(cell.index)
              onToggleTick(cell.index)
            }}>
            <span
              className={`tmg-checkbox${isTicked ? ' checked' : ''}`}
              role='checkbox'
              aria-checked={isTicked}>
              {isTicked ? (
                <svg
                  className='tmg-checkbox-x'
                  viewBox='0 0 12 12'
                  width='12'
                  height='12'
                  focusable='false'>
                  <path
                    d='M2 2l8 8M10 2L2 10'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              ) : null}
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable className='tmg-scrollable'>
        <div
          ref={ref}
          className={`survey-question timed-matching-grid tmg-input-${inputType}${
            className ? ` ${className}` : ''
          }${readOnly ? ' is-locked' : ''}`}>
          {!(locked || isLocked) && isActiveQuestion && (
            <div className='tmg-header'>
              <div className='tmg-timer' aria-live='polite'>
                <span className='tmg-timer-value'>{timerLabel}</span>
              </div>
              <div className='tmg-counter' aria-live='polite'>
                {counterText}
              </div>
            </div>
          )}

          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>

          <div
            className='tmg-key'
            style={{ '--tmg-key-cols': Math.max(1, symbolKey.length || 9) }}
            aria-label='Symbol key'>
            <div className='tmg-key-row tmg-key-symbols'>
              {symbolKey.map(entry => (
                <div
                  className='tmg-key-cell tmg-key-symbol'
                  key={`key-symbol-${entry.digit}`}>
                  <SymbolContent entry={entry} />
                </div>
              ))}
            </div>
            <div className='tmg-key-row tmg-key-digits'>
              {symbolKey.map(entry => (
                <div
                  className='tmg-key-cell tmg-key-digit'
                  key={`key-digit-${entry.digit}`}>
                  {entry.digit}
                </div>
              ))}
            </div>
          </div>

          <p className='tmg-guidance'>{guidance}</p>

          <div className='tmg-grid-scroll' ref={gridScrollRef}>
            <div
              className={`tmg-grid${
                inputType === INPUT_TYPES.checkmark
                  ? ' tmg-grid--checkmark'
                  : ''
              }`}
              style={{ '--tmg-cols': gridColumns }}
              role='grid'
              aria-disabled={readOnly || undefined}
              aria-rowcount={gridRows}
              aria-colcount={gridColumns}>
              {Array.from({ length: gridRows }, (_, rowIndex) => (
                <div
                  key={`tmg-row-${rowIndex}`}
                  className='tmg-grid-row'
                  role='row'>
                  {cells
                    .slice(rowIndex * gridColumns, (rowIndex + 1) * gridColumns)
                    .map(renderCell)}
                </div>
              ))}
            </div>
          </div>

          {!isSibling && (
            <div className='character-count-row'>
              <QuestionFooter
                completed={canAdvance}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() => {
                  // GOLD-12178: Footer Next is allowed when the grid is
                  // full or the timer ended. Reset the submit guard so
                  // a second click after a failed complete can retry.
                  if (!canAdvance) return
                  hasSubmittedRef.current = false
                  submitAndAdvance(!!timedOut)
                }}
                question={{
                  ...question,
                  attributes: {
                    ...(attributes || {}),
                    required: true,
                    in_review: false
                  }
                }}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            </div>
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

TimedMatchingGrid.displayName = 'TimedMatchingGrid'

export default TimedMatchingGrid

TimedMatchingGrid.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  isConsent: PropTypes.bool,
  isLastQuestion: PropTypes.bool,
  isSibling: PropTypes.bool,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  storedAnswer: PropTypes.array,
  submissionLoading: PropTypes.bool,
  submissionSurvey: PropTypes.object,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  isActiveQuestion: PropTypes.bool,
  suppressAutoAdvance: PropTypes.bool,
  webSurvey: PropTypes.object
}
